/**
 * Service entity adapter (P15.2 / #416).
 *
 * Services have a self-referential `parent_service_id` for two-level
 * trees. The parent picker is rendered via `parentSelect` form field,
 * which fetches all services into a flat select. List-table parent
 * column shows the parent's name (resolved client-side from the same
 * list payload).
 */
import type { AdminColumn, EntityAdapter, FormField } from "./types";
import { ServiceAnalyserSection } from "./ServiceAnalyserSection";
import { ServiceClassificationDecisionsSection } from "./serviceClassificationDecisions";

export type ServiceClassification =
  "ibs_supporting" | "supporting" | "non_critical" | "out_of_scope";

export type ServiceBizCrit = "Tier 0" | "Tier 1" | "Tier 2" | "Tier 3";
export type ServiceCyberCrit = "High" | "Medium" | "Low";

export interface Service {
  id: string;
  code: string;
  name: string;
  description: string | null;
  classification: ServiceClassification;
  // P37 methodology fields (all nullable; CMDB rollup back-fills,
  // P38 Engine fills remaining gaps)
  biz_crit: ServiceBizCrit | null;
  cyber_crit: ServiceCyberCrit | null;
  rto_hours: number | null;
  rpo_hours: number | null;
  financial_impact_sar: string | null; // Numeric serialises as string
  parent_service_id: string | null;
  // P136 (P117f.4): implied-SSOT service-profile fields.
  lifecycle_stage: string | null;
  hosting_model: string | null;
  data_classification: string | null;
  service_owner: string | null;
  recovery_tier: string | null;
  // P367: continuity posture (MVB gap-analysis F3).
  continuity_posture: string | null;
  continuity_last_proven_on: string | null;
  continuity_evidence_ref: string | null;
  // P440: the cost bases the MVB target state prices gaps with.
  hardening_cost_sar?: string | null;
  hardened_rto_hours?: number | null;
  posture_upgrade_cost_sar?: string | null;
  /** Round 19 L14 -- the bank's own mark of an internal tool. */
  internal_tool?: boolean;
  /** Round 19 G40 -- the business services resting on this one. */
  rests_on_it?: string[];
  created_at: string;
  updated_at: string;
}

export interface ServiceIn {
  code: string;
  name: string;
  description: string | null;
  classification: ServiceClassification;
  biz_crit?: ServiceBizCrit | null;
  cyber_crit?: ServiceCyberCrit | null;
  rto_hours?: number | null;
  rpo_hours?: number | null;
  financial_impact_sar?: string | number | null;
  parent_service_id: string | null;
  lifecycle_stage?: string | null;
  hosting_model?: string | null;
  data_classification?: string | null;
  service_owner?: string | null;
  recovery_tier?: string | null;
  continuity_posture?: string | null;
  continuity_last_proven_on?: string | null;
  continuity_evidence_ref?: string | null;
  hardening_cost_sar?: string | number | null;
  hardened_rto_hours?: number | null;
  posture_upgrade_cost_sar?: string | number | null;
  internal_tool?: boolean;
}

export type ServicePatch = Partial<ServiceIn>;

const CLASSIFICATION_OPTIONS = [
  { value: "ibs_supporting", label: "IBS-supporting" },
  { value: "supporting", label: "Supporting" },
  { value: "non_critical", label: "Non-critical" },
  { value: "out_of_scope", label: "Out of scope" },
];

const CLASSIFICATION_TONE: Record<ServiceClassification, string> = {
  ibs_supporting: "bg-emerald-100 text-emerald-900",
  supporting: "bg-slate-100 text-slate-800",
  non_critical: "bg-amber-100 text-amber-900",
  out_of_scope: "bg-slate-100 text-slate-500",
};

const columns: AdminColumn<Service>[] = [
  {
    key: "code",
    label: "Code",
    sortValue: (r) => r.code,
    render: (r) => <span className="font-mono text-xs text-slate-700">{r.code}</span>,
  },
  {
    key: "name",
    label: "Name",
    sortValue: (r) => r.name,
    render: (r) => <span className="font-medium text-slate-900">{r.name}</span>,
  },
  {
    key: "classification",
    label: "Classification",
    sortValue: (r) => r.classification,
    render: (r) => (
      <span
        data-testid="service-classification-chip"
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${
          CLASSIFICATION_TONE[r.classification]
        }`}
      >
        {r.classification.replace(/_/g, " ")}
      </span>
    ),
  },
  /* Round 19 G40 -- Services said nothing of what rests on a service,
     so an owner choosing one could not tell which of his business
     services a change to it would touch. */
  {
    key: "rests_on_it",
    label: "Rests on it",
    sortValue: (r) => (r.rests_on_it ?? []).length,
    render: (r) =>
      (r.rests_on_it ?? []).length > 0 ? (
        <span data-testid="service-rests-on-it" className="text-xs text-slate-700">
          {(r.rests_on_it ?? []).join(", ")}
        </span>
      ) : (
        <span className="text-xs text-slate-400">--</span>
      ),
  },
  {
    key: "parent_service_id",
    label: "Parent",
    render: (r) =>
      r.parent_service_id ? (
        <span
          data-testid="service-parent-cell"
          className="font-mono text-xs text-slate-500"
          title={r.parent_service_id}
        >
          {r.parent_service_id.slice(0, 8)}...
        </span>
      ) : (
        <span className="text-xs text-slate-400">--</span>
      ),
  },
];

const BIZ_CRIT_OPTIONS = [
  { value: "Tier 0", label: "Tier 0" },
  { value: "Tier 1", label: "Tier 1" },
  { value: "Tier 2", label: "Tier 2" },
  { value: "Tier 3", label: "Tier 3" },
];
const CYBER_CRIT_OPTIONS = [
  { value: "High", label: "High" },
  { value: "Medium", label: "Medium" },
  { value: "Low", label: "Low" },
];

const METHODOLOGY = "Methodology (SAMA)";
const PROFILE = "Service profile";
const CONTINUITY = "Continuity";
const COST_BASIS = "Cost basis";
const CONTINUITY_POSTURE_OPTIONS = [
  { value: "active_active", label: "Active-active" },
  { value: "active_passive", label: "Active-passive" },
  { value: "backup_restore", label: "Backup-restore" },
  { value: "manual_workaround", label: "Manual workaround" },
  { value: "none", label: "None" },
];

// Alembic 0235 -- the lifecycle stage is a closed vocabulary.
const LIFECYCLE_STAGE_OPTIONS = [
  { value: "planned", label: "Planned" },
  { value: "active", label: "Active" },
  { value: "retiring", label: "Retiring" },
  { value: "retired", label: "Retired" },
];

const formFields: FormField<ServiceIn>[] = [
  { kind: "text", key: "code", label: "Code", required: true, maxLength: 64 },
  { kind: "text", key: "name", label: "Name", required: true, maxLength: 255 },
  { kind: "textarea", key: "description", label: "Description", rows: 3 },
  {
    kind: "select",
    key: "classification",
    label: "Classification",
    required: true,
    options: CLASSIFICATION_OPTIONS,
  },
  {
    kind: "parentSelect",
    key: "parent_service_id",
    label: "Parent service",
    entityKind: "service",
    nullable: true,
  },
  // P37 methodology (CMDB rollup / Engine back-fill; editable here too).
  {
    kind: "select",
    key: "biz_crit",
    label: "Business criticality",
    options: BIZ_CRIT_OPTIONS,
    group: METHODOLOGY,
  },
  {
    kind: "select",
    key: "cyber_crit",
    label: "Cyber criticality",
    options: CYBER_CRIT_OPTIONS,
    group: METHODOLOGY,
  },
  {
    kind: "number",
    key: "rto_hours",
    label: "RTO (hours)",
    min: 0,
    group: METHODOLOGY,
  },
  {
    kind: "number",
    key: "rpo_hours",
    label: "RPO (hours)",
    min: 0,
    group: METHODOLOGY,
  },
  {
    kind: "decimal",
    key: "financial_impact_sar",
    label: "Financial impact",
    currencyHint: "SAR",
    group: METHODOLOGY,
  },
  // Implied-SSOT service profile (P136 / P117f.4).
  {
    kind: "select",
    key: "lifecycle_stage",
    label: "Lifecycle stage",
    options: LIFECYCLE_STAGE_OPTIONS,
    group: PROFILE,
  },
  {
    kind: "text",
    key: "hosting_model",
    label: "Hosting model",
    maxLength: 32,
    group: PROFILE,
  },
  {
    kind: "text",
    key: "data_classification",
    label: "Data classification",
    maxLength: 32,
    group: PROFILE,
  },
  {
    kind: "text",
    key: "service_owner",
    label: "Service owner",
    maxLength: 255,
    group: PROFILE,
  },
  {
    kind: "text",
    key: "recovery_tier",
    label: "Recovery tier",
    maxLength: 32,
    group: PROFILE,
  },
  // P367 continuity posture (MVB gap-analysis F3).
  {
    kind: "select",
    key: "continuity_posture",
    label: "Continuity posture",
    options: CONTINUITY_POSTURE_OPTIONS,
    group: CONTINUITY,
  },
  {
    kind: "date",
    key: "continuity_last_proven_on",
    label: "Posture last proven on",
    group: CONTINUITY,
  },
  {
    kind: "text",
    key: "continuity_evidence_ref",
    label: "Proof evidence reference",
    group: CONTINUITY,
  },
  // P440: what closing this service's MVB gaps would cost.
  {
    kind: "decimal",
    key: "hardening_cost_sar",
    label: "Hardening cost",
    currencyHint: "SAR",
    group: COST_BASIS,
  },
  {
    kind: "number",
    key: "hardened_rto_hours",
    label: "RTO the hardening buys (hours)",
    min: 0,
    group: COST_BASIS,
  },
  {
    kind: "decimal",
    key: "posture_upgrade_cost_sar",
    label: "Continuity posture upgrade cost",
    currencyHint: "SAR",
    group: COST_BASIS,
  },
  /* Round 19 L14 -- held back where a function is rested on its
     services; "not critical" no longer does that on its own. */
  {
    kind: "bool",
    key: "internal_tool",
    label: "Internal tool (canteen, wiki, blog...)",
  },
];

export const serviceAdapter: EntityAdapter<Service, ServiceIn, ServicePatch> = {
  i18nPrefix: "modules.foundation.admin.services",
  importKind: "service",
  endpoint: "/v1/admin/foundation/services",
  queryKey: ["foundation-admin", "services"] as const,
  columns,
  defaultSort: { key: "code", dir: "asc" },
  rowKey: (r) => r.id,
  rowSearchHaystack: (r) => `${r.code} ${r.name} ${r.description ?? ""}`,
  formFields,
  formInitial: () => ({
    code: "",
    name: "",
    description: null,
    classification: "supporting",
    parent_service_id: null,
    biz_crit: null,
    cyber_crit: null,
    rto_hours: null,
    rpo_hours: null,
    financial_impact_sar: null,
    lifecycle_stage: null,
    hosting_model: null,
    data_classification: null,
    service_owner: null,
    recovery_tier: null,
    continuity_posture: null,
    continuity_last_proven_on: null,
    continuity_evidence_ref: null,
    hardening_cost_sar: null,
    hardened_rto_hours: null,
    posture_upgrade_cost_sar: null,
    internal_tool: false,
  }),
  rowToPatch: (r) => ({
    code: r.code,
    name: r.name,
    description: r.description,
    classification: r.classification,
    biz_crit: r.biz_crit,
    cyber_crit: r.cyber_crit,
    rto_hours: r.rto_hours,
    rpo_hours: r.rpo_hours,
    financial_impact_sar: r.financial_impact_sar,
    parent_service_id: r.parent_service_id,
    lifecycle_stage: r.lifecycle_stage,
    hosting_model: r.hosting_model,
    data_classification: r.data_classification,
    service_owner: r.service_owner,
    recovery_tier: r.recovery_tier,
    continuity_posture: r.continuity_posture,
    continuity_last_proven_on: r.continuity_last_proven_on,
    continuity_evidence_ref: r.continuity_evidence_ref,
    hardening_cost_sar: r.hardening_cost_sar ?? null,
    hardened_rto_hours: r.hardened_rto_hours ?? null,
    posture_upgrade_cost_sar: r.posture_upgrade_cost_sar ?? null,
    internal_tool: r.internal_tool ?? false,
  }),
  // P38.4: Engine analyser surface lives as an extra drawer
  // section. Button + run history + suggestion review modal.
  // P73.2: Classification decisions ledger + dual signoff.
  extraSections: [ServiceAnalyserSection, ServiceClassificationDecisionsSection],
};
